import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ArrowUpRight, History, Users} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {formatMoney, formatNumber} from "../../shared/utils/format";
import {fillReferralUser, getPlaceReferralHistory, referralKeys} from "./api";
import {normalizePlaceHistory, validateFillDraft} from "./contracts";
import {ReferralDialog, ReferralDialogError} from "./ReferralDialog";
import {useTranslation} from "../../shared/i18n";


const newIdempotencyKey = () => {
    if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
        return `ref-fill-${window.crypto.randomUUID()}`;
    }
    return `ref-fill-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const FillDialog = ({open, countryId, balance, onClose, onCompleted}) => {
    const {t} = useTranslation();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [amount, setAmount] = useState("");
    const [preview, setPreview] = useState(null);
    const [validationError, setValidationError] = useState("");
    const [idempotencyKey, setIdempotencyKey] = useState("");
    const mutation = useMutation({
        mutationFn: () => fillReferralUser({phoneNumber, amount, countryId, idempotencyKey}),
        onSuccess: () => onCompleted(),
    });
    const resetMutation = mutation.reset;

    useEffect(() => {
        if (!open) return;
        setPhoneNumber("");
        setAmount("");
        setPreview(null);
        setValidationError("");
        setIdempotencyKey(newIdempotencyKey());
        resetMutation();
    }, [open, resetMutation]);

    const inspect = (event) => {
        event.preventDefault();
        const validation = validateFillDraft({phoneNumber, amount});
        setValidationError(validation.error);
        if (!validation.error) setPreview(validation.values);
    };

    return (
        <ReferralDialog open={open} title={t("sections.referrals.p1")} eyebrow={t("sections.referrals.p2", {v0: formatMoney(balance)})} pending={mutation.isPending} onClose={onClose}>
            <form className="referral-dialog-form" onSubmit={inspect}>
                <div className="referral-field-row">
                    <label className="referral-field">
                        <span>{t("sections.referrals.p3")}</span>
                        <input value={phoneNumber} disabled={mutation.isPending} onChange={(event) => { setPhoneNumber(event.target.value); setPreview(null); }} />
                    </label>
                    <label className="referral-field">
                        <span>{t("sections.referrals.p4")}</span>
                        <input inputMode="numeric" value={amount} disabled={mutation.isPending} onChange={(event) => { setAmount(event.target.value); setPreview(null); }} />
                    </label>
                </div>
                {preview ? (
                    <div className="referral-command-preview" role="status">
                        <strong>{t("sections.referrals.p5")}</strong>
                        <p>{preview.ref_sum} {t("sections.referrals.p6")} {preview.phone_number}</p>
                    </div>
                ) : null}
                {validationError ? <div className="referral-dialog-error" role="alert">{validationError}</div> : null}
                <ReferralDialogError result={mutation.isError ? mapApiError(mutation.error) : null} />
                <footer className="referral-dialog-footer">
                    <button type="button" className="admin-button admin-button-secondary" disabled={mutation.isPending} onClick={onClose}>{t("sections.referrals.p7")}</button>
                    {preview ? (
                        <button type="button" className="admin-button admin-button-primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                            {mutation.isPending ? t("sections.referrals.p8") : t("sections.referrals.p9")}
                        </button>
                    ) : (
                        <button type="submit" className="admin-button admin-button-primary">{t("sections.referrals.p10")}</button>
                    )}
                </footer>
            </form>
        </ReferralDialog>
    );
};

export const PlaceReferralWorkspace = ({countryId, canManage}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [range, setRange] = useState("месяц");
    const [fillOpen, setFillOpen] = useState(false);
    const [result, setResult] = useState(null);
    const historyQuery = useQuery({
        queryKey: referralKeys.placeHistory(countryId, range),
        queryFn: () => getPlaceReferralHistory({range, countryId}),
    });
    const history = normalizePlaceHistory(historyQuery.data);

    const completed = () => {
        setFillOpen(false);
        setResult({kind: "success", message: t("sections.referrals.p11")});
        void queryClient.invalidateQueries({queryKey: ["admin-referrals", String(countryId ?? ""), "place-history"]});
    };

    const columns = [
        {key: "type", label: t("sections.referrals.p12")},
        {key: "amount", label: t("sections.referrals.p4"), render: (item) => formatMoney(item.amount)},
        {key: "user", label: t("sections.referrals.p13")},
        {key: "date", label: t("sections.referrals.p14")},
    ];

    return (
        <div className="admin-page referral-place-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("sections.referrals.p15")}</p>
                    <h1 className="admin-page-title">{t("sections.referrals.p16")}</h1>
                    <p className="admin-page-copy">{t("sections.referrals.p17")}</p>
                </div>
                {canManage ? (
                    <button type="button" className="admin-button admin-button-primary" onClick={() => { setResult(null); setFillOpen(true); }}>
                        <ArrowUpRight aria-hidden="true" size={16} /> {t("sections.referrals.p18")}
                    </button>
                ) : null}
            </header>
            {result ? <MutationResultBanner result={result} /> : null}
            {historyQuery.isError ? (
                <DataState title={t("sections.referrals.p19")} copy={mapApiError(historyQuery.error).message} />
            ) : (
                <>
                    <section className="admin-grid cols-3">
                        <article className="admin-card">
                            <p className="admin-card-title">{t("sections.referrals.p20")}</p>
                            <p className="admin-card-value">{formatMoney(history.balance)}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><Users size={14} /> {t("sections.referrals.p21")}</p>
                            <p className="admin-card-value">{formatNumber(history.invitedCount)}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><History size={14} /> {t("sections.referrals.p22")}</p>
                            <p className="admin-card-value">{formatNumber(history.networkCount)}</p>
                        </article>
                    </section>
                    <div className="referral-period-row">
                        <label className="admin-field">
                            <span>{t("sections.referrals.p23")}</span>
                            <select value={range} onChange={(event) => setRange(event.target.value)}>
                                <option value="день">{t("sections.referrals.p24")}</option>
                                <option value="неделя">{t("sections.referrals.p25")}</option>
                                <option value="месяц">{t("sections.referrals.p26")}</option>
                            </select>
                        </label>
                    </div>
                    <AdminTable columns={columns} rows={history.items} rowKey="date" loading={historyQuery.isLoading} emptyText={t("sections.referrals.p27")} />
                </>
            )}
            <FillDialog open={fillOpen} countryId={countryId} balance={history.balance} onClose={() => setFillOpen(false)} onCompleted={completed} />
        </div>
    );
};
